import React, { useState } from "react";
import { Box, Typography, Grid, Snackbar, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import AnalyticsTabs from "./AnalyticsTabs";
import DateTabs from "./DateTabs";
import {
  useProgressiveOverviewData,
  ChunkLoadingIndicator,
} from "./useProgressiveOverviewData";
import { CUSTOM_CHART_CONFIGS } from "./chartConfigs";
import { getTabConfigurations } from "./tabConfigs";
import LazyChart from "./LazyChart";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router";

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

export default function AnalyticsChart({
  vehiclesData,
  routesData,
  driversData,
  stopsData,
  sitesData,
  usersData,
  overview = false,
  school = false,
  hideHeader = false,
  hideInternalTabs = false,
  activeTab: activeTabProp,
  selectedDateRange: selectedDateRangeProp,
  customDateRange: customDateRangeProp,
  onViewMore,
}) {
  const { t } = useTranslation();
  const [internalActiveTab, setInternalActiveTab] = useState(0);
  const [internalDateRange, setInternalDateRange] = useState("today");
  const [internalCustomDateRange, setInternalCustomDateRange] = useState(null);
  const navigate = useNavigate();
  const isTabControlled = activeTabProp !== undefined;
  const isDateControlled = selectedDateRangeProp !== undefined;
  const activeTab = isTabControlled ? activeTabProp : internalActiveTab;
  const selectedDateRange = isDateControlled
    ? selectedDateRangeProp
    : internalDateRange;
  const customDateRange = isDateControlled
    ? customDateRangeProp
    : internalCustomDateRange;
  
  const {
    chartDataStates,
    loadingStates,
    error: overviewError,
    chunkProgress,
    isCancelling,
  } = useProgressiveOverviewData(selectedDateRange, customDateRange);

  const handleTabChange = (event, newValue) => {
    if (!isTabControlled) {
      setInternalActiveTab(newValue);
    }
  };

  const handleDateRangeChange = (rangeType, dateRange) => {
    if (isDateControlled) {
      return;
    }
    setInternalDateRange(rangeType);
    setInternalCustomDateRange(dateRange);
  };

  // Process charts with their data states
  const processedCharts = CUSTOM_CHART_CONFIGS.map((chartConfig) => {
    const dataKey = `${chartConfig.reportType}_${chartConfig.category}`;
    const chartState = chartDataStates[dataKey] || {
      loading: true,
      data: null,
      error: null,
    };

    return {
      ...chartConfig,
      chartState,
    };
  });

  // Get trip data for generating route-specific charts
  const tripDataState = chartDataStates["trip_vehicles"];
  const tripReportData = tripDataState?.data;

  // Get tab configurations with trip data for route-specific charts
  const allTabs = getTabConfigurations(t, processedCharts, tripReportData);

  // Get only first 4 charts for overview mode
  const displayedCharts = overview
    ? processedCharts.slice(0, 4)
    : processedCharts;

  return (
    <Box sx={{ p: hideHeader ? 0 : 2, width: "100%" }} className="bg-gray-100">
      {/* Cancellation notification */}
      {/* <Snackbar
        open={isCancelling}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert severity="info" sx={{ width: '100%' }}>
          Cancelling previous requests and loading new data...
        </Alert>
      </Snackbar> */}

      {!overviewError && !hideHeader && (
        <Box
          sx={{
            background: "#647490",
            color: "#fff",
            fontSize: "24px",
            p: 2,
            borderRadius: "8px",
            mb: 2,
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            boxShadow: "-2px 2px 5px 0px #00000040",
          }}
          className="shadow-lg"
        >
          {t("Analytics")} {overview && t("Overview")}
        </Box>
      )}

      {overviewError && (
        <Box sx={{ mb: 2, p: 2, bgcolor: "#ffebee", borderRadius: 1 }}>
          <Typography color="error">{overviewError}</Typography>
        </Box>
      )}

      {overview === false && !hideInternalTabs && (
        <AnalyticsTabs
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          allTabs={allTabs}
        />
      )}

      {overview === false && !hideInternalTabs && !isDateControlled && (
        <DateTabs
          onDateRangeChange={handleDateRangeChange}
          selectedDateRange={selectedDateRange}
        />
      )}

      {/* OVERVIEW MODE - Show single row grid with first 4 charts */}
      {overview && (
        <>
          <Grid container spacing={2} justifyContent="center">
            {displayedCharts
              .filter(
                (chartConfig) =>
                  chartConfig.id !== "trip_gantt_timeline" &&
                  chartConfig.id !== "overspeed_timeline"
              )
              .map((chartConfig, chartIndex) => {
                const dataKey = `${chartConfig.reportType}_${chartConfig.category}`;
                const progress = chunkProgress[dataKey];

                return (
                  <Grid item xs={12} md={3} lg={3} key={`chart-${chartIndex}`}>
                    <Box
                      sx={{
                        p: 1.5,
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        backgroundColor: hideHeader ? "#f4f4f5" : "white",
                        minHeight: "320px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        position: "relative",
                        opacity: isCancelling ? 0.5 : 1,
                        transition: "opacity 0.3s ease",
                        "&:hover": {
                          transform: isCancelling ? "none" : "translateY(-2px)",
                          boxShadow: isCancelling ? "0 1px 3px rgba(0,0,0,0.1)" : "0 3px 6px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      {progress && progress.percentComplete < 100 && !isCancelling && (
                        <ChunkLoadingIndicator progress={progress} />
                      )}

                      <LazyChart
                        chartConfig={{
                          ...chartConfig,
                          width: "100%",
                          height: 260,
                        }}
                        chartState={chartConfig.chartState}
                        loadingState={loadingStates[dataKey]}
                        vehiclesData={vehiclesData}
                        selectedDateRange={selectedDateRange}
                      />
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
          
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              onClick={() => {
                if (onViewMore) {
                  onViewMore();
                  return;
                }
                navigate("/reports/analytics");
              }}
              sx={{
                width: "143px",
                height: "55px",
                mt: 3,
                borderRadius: "8px",
                backgroundColor: "#F9E719",
                color: "#1F242E",
                textTransform: "none",
                border: "1px solid #414958",
                fontWeight: 600,
                fontFamily: "Roboto, sans-serif",
                fontSize: "20px",
                "&:hover": {
                  backgroundColor: "#F9E719",
                },
              }}
            >
              {t("View More")}
            </Button>
          </Box>
        </>
      )}

      {/* FULL ANALYTICS MODE - Show tabbed charts */}
      {!overview && (
        <>
          {allTabs.map((tab, index) => (
            <TabPanel key={index} value={activeTab} index={index}>
              {/* Regular grid layout for other charts */}
              <Grid container spacing={2} justifyContent="center">
                {tab.charts
                  .filter(
                    (chartConfig) =>
                      chartConfig.id !== "trip_gantt_timeline" &&
                      chartConfig.id !== "overspeed_timeline"
                  )
                  .map((chartConfig, chartIndex) => {
                    const dataKey = `${chartConfig.reportType}_${chartConfig.category}`;
                    const progress = chunkProgress[dataKey];

                    return (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={4}
                        key={`chart-${index}-${chartIndex}`}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            backgroundColor: hideHeader ? "#f4f4f5" : "white",
                            minHeight: "320px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            position: "relative",
                            opacity: isCancelling ? 0.5 : 1,
                            transition: "opacity 0.3s ease",
                            "&:hover": {
                              transform: isCancelling ? "none" : "translateY(-2px)",
                              boxShadow: isCancelling ? "0 1px 3px rgba(0,0,0,0.1)" : "0 3px 6px rgba(0,0,0,0.15)",
                            },
                          }}
                        >
                          {/* Loading indicator in top-right corner */}
                          {progress && progress.percentComplete < 100 && !isCancelling && (
                            <ChunkLoadingIndicator progress={progress} />
                          )}

                          <LazyChart
                            chartConfig={{
                              ...chartConfig,
                              width: "100%",
                              height: 260,
                            }}
                            chartState={chartConfig.chartState}
                            loadingState={loadingStates[dataKey]}
                            vehiclesData={vehiclesData}
                            selectedDateRange={selectedDateRange}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
              </Grid>

              {/* Separate full-width timeline charts */}
              {tab.charts
                .filter(
                  (chartConfig) =>
                    chartConfig.id === "trip_gantt_timeline" ||
                    chartConfig.id === "overspeed_timeline"
                )
                .map((chartConfig, chartIndex) => {
                  const dataKey = `${chartConfig.reportType}_${chartConfig.category}`;
                  const progress = chunkProgress[dataKey];

                  return (
                    <Box
                      key={`timeline-${index}-${chartIndex}`}
                      sx={{
                        mt: 2,
                        mb: 3,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        backgroundColor: hideHeader ? "#f4f4f5" : "white",
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        position: "relative",
                        opacity: isCancelling ? 0.5 : 1,
                        transition: "opacity 0.3s ease, transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: isCancelling ? "none" : "translateY(-2px)",
                          boxShadow: isCancelling ? "0 2px 4px rgba(0,0,0,0.1)" : "0 4px 8px rgba(0,0,0,0.15)",
                        },
                      }}
                      className="hover:shadow-xl"
                    >
                      {/* Loading indicator for timeline charts */}
                      {progress && progress.percentComplete < 100 && !isCancelling && (
                        <ChunkLoadingIndicator progress={progress} />
                      )}

                      <LazyChart
                        chartConfig={{
                          ...chartConfig,
                          width: "100%",
                          height: 350,
                        }}
                        chartState={chartConfig.chartState}
                        vehiclesData={vehiclesData}
                        selectedDateRange={selectedDateRange}
                        isTimelineChart={true}
                      />
                    </Box>
                  );
                })}

              {tab.charts.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    {t("No charts available for")} {tab.label}
                  </Typography>
                </Box>
              )}
            </TabPanel>
          ))}
        </>
      )}
    </Box>
  );
}
